'use strict';

Object.defineProperty(exports, "__esModule", {
    value: true
});
exports.createFactory = undefined;

var _extends2 = require('babel-runtime/helpers/extends');

var _extends3 = _interopRequireDefault(_extends2);

var _objectWithoutProperties2 = require('babel-runtime/helpers/objectWithoutProperties');

var _objectWithoutProperties3 = _interopRequireDefault(_objectWithoutProperties2);

var _promise = require('babel-runtime/core-js/promise');

var _promise2 = _interopRequireDefault(_promise);

exports.default = createPlacement;

var _attributes = require('./attributes');

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function createPlacement(_ref) {
    var node = _ref.node,
        appnexus = _ref.appnexus,
        observer = _ref.observer,
        margins = _ref.margins,
        _ref$refreshable = _ref.refreshable,
        refreshable = _ref$refreshable === undefined ? false : _ref$refreshable,
        nativeRenderer = _ref.nativeRenderer;

    var targetId = (0, _attributes.getId)(node);
    var nodeObservers = [];
    var subscribers = [];
    var savedEvents = [];
    var renderTime = void 0;
    var isNative = void 0;
    var ad = void 0;

    return {
        enable: enable,
        subscribe: subscribe,
        isRendered: isRendered,
        destroy: destroy
    };

    function enable() {
        appnexus.onEvent(targetId, {
            adRequested: _onAdRequested,
            adAvailable: _onAdAvailable,
            adBadRequest: _onAdBadRequest,
            adLoaded: _onAdLoaded,
            adNoBid: _onAdNoBid,
            adRequestFailure: _onAdRequestFailure,
            adError: _onAdError,
            adCollapse: _onAdCollapse
        });
        appnexus.defineTag(node);

        _setupRenderObserver();
        if (refreshable) {
            _setupRefreshObserver();
        }

        return this;
    }

    function isRendered() {
        return renderTime !== undefined;
    }

    function subscribe(eventHandlerMap) {
        subscribers.push(eventHandlerMap);
        var subscription = {
            unsubscribe: function unsubscribe() {
                subscribers = subscribers.filter(function (cb) {
                    return cb !== eventHandlerMap;
                });
            }
        };
        replayHistory(savedEvents, eventHandlerMap).catch(function (e) {
            if (__DEV__) {
                console.error('Failed to replay event history for placement', e); // eslint-disable-line no-console
            }
            subscription.unsubscribe();
        });
        return subscription;
    }

    function destroy() {
        subscribers = [];
        savedEvents = [];
        nodeObservers.forEach(function (nodeObserver) {
            return nodeObserver.unsubscribe();
        });
        appnexus.offEvent(targetId, {
            adRequested: _onAdRequested,
            adAvailable: _onAdAvailable,
            adBadRequest: _onAdBadRequest,
            adLoaded: _onAdLoaded,
            adNoBid: _onAdNoBid,
            adRequestFailure: _onAdRequestFailure,
            adError: _onAdError,
            adCollapse: _onAdCollapse
        });
    }

    // Private functions
    function _renderNativeAd() {
        var placementSubscription = subscribe({
            onAdAvailable: function onAdAvailable() {
                if (isNative) {
                    _promise2.default.resolve().then(function () {
                        return nativeRenderer(ad);
                    }).then(function (fragment) {
                        if (fragment) {
                            // Reset min-height if it's a native ad
                            node.style.minHeight = null; // eslint-disable-line no-param-reassign
                            node.appendChild(fragment);
                            appnexus.fireImpressionTrackers(ad);
                        }
                    }).catch(function (e) {
                        _onAdError({ code: -1, errMessage: 'Native rendering failed', exception: e, targetId: targetId }, ad);
                    });
                }
                placementSubscription.unsubscribe();
            },
            onError: function onError() {
                placementSubscription.unsubscribe();
            }
        });
    }

    /**
     * @private
     */
    function _setupRenderObserver() {
        var renderObserver = observer.subscribe(node, {
            margin: margins.renderMargin,
            onChange: function onChange(_ref2) {
                var id = _ref2.id,
                    currentState = _ref2.currentState;

                if (currentState) {
                    appnexus.showTag(id);
                    _renderNativeAd();
                    renderObserver.unsubscribe();
                }
            }
        });
        nodeObservers.push(renderObserver);
    }

    /**
     * @private
     */
    function _setupRefreshObserver() {
        nodeObservers.push(observer.subscribe(node, {
            margin: margins.refreshMargin,
            onChange: function onChange(_ref3) {
                var id = _ref3.id,
                    currentState = _ref3.currentState;

                if (currentState && _validForRefresh(10000)) {
                    appnexus.refresh(id);
                }
            }
        }));
    }

    /**
     * @private
     */
    function _notifySubscribers(_ref4) {
        var type = _ref4.type,
            args = (0, _objectWithoutProperties3.default)(_ref4, ['type']);

        return _promise2.default.resolve().then(function () {
            subscribers.forEach(function (eventHandlerMap) {
                if (typeof eventHandlerMap[type] === 'function') {
                    eventHandlerMap[type]((0, _extends3.default)({}, args, { type: type }));
                }
            });
        });
    }

    /**
     * @private
     */
    function _validForRefresh(minDisplayTime) {
        return isRendered() && renderTime + minDisplayTime < new Date().getTime();
    }

    /**
     * @private
     */
    function _tagStateChanged(state) {
        var notifyStates = [state];

        switch (state.type) {
            case 'onAdRequested':
                // Clear out event chain when the ad is requested
                // (Might happen multiple times in case of refreshable ads)
                savedEvents = [];
                renderTime = undefined;
                isNative = undefined;
                break;
            case 'onAdAvailable':
                {
                    ad = state.adObj || {};
                    isNative = ad.adType === 'native';
                    break;
                }
            case 'onAdLoaded':
                renderTime = new Date().getTime();
                notifyStates.push((0, _extends3.default)({}, state, {
                    type: 'onSuccess'
                }));
                break;
            case 'onAdBadRequest':
            case 'onAdNoBid':
            case 'onAdRequestFailure':
                notifyStates.push((0, _extends3.default)({}, state, {
                    type: 'onError'
                }));
                break;
            default:
                break;
        }
        savedEvents = savedEvents.concat(notifyStates);
        return _promise2.default.all(notifyStates.map(function (s) {
            return _notifySubscribers(s);
        }));
    }

    /**
     * @private
     */
    function _onAdRequested() {
        _tagStateChanged({ type: 'onAdRequested' });
    }

    /**
     * @private
     */
    function _onAdAvailable(adObj) {
        _tagStateChanged({ type: 'onAdAvailable', adObj: adObj });
    }

    /**
     * @private
     */
    function _onAdBadRequest(adError) {
        _tagStateChanged({ type: 'onAdBadRequest', adError: adError });
    }

    /**
     * @private
     */
    function _onAdNoBid(adObj) {
        _tagStateChanged({ type: 'onAdNoBid', adObj: adObj });
    }

    /**
     * @private
     */
    function _onAdLoaded(adObj) {
        _tagStateChanged({ type: 'onAdLoaded', adObj: adObj });
    }

    /**
     * @private
     */
    function _onAdRequestFailure(adError) {
        _tagStateChanged({ type: 'onAdRequestFailure', adError: adError });
    }

    /**
     * @private
     */
    function _onAdError(adError, adObj) {
        _tagStateChanged({ type: 'onAdError', adError: adError, adObj: adObj });
    }

    /**
     * @private
     */
    function _onAdCollapse() {
        _tagStateChanged({ type: 'onAdCollapse' });
    }
} /* global __DEV__ */
/* eslint-disable no-underscore-dangle */

function replayHistory(events, callbacks) {
    return _promise2.default.resolve().then(function () {
        events.forEach(function (savedEvent) {
            var cb = callbacks[savedEvent.type];
            if (typeof cb === 'function') {
                cb({ args: savedEvent.args });
            }
        });
    });
}

var getMarginsForPriority = function getMarginsForPriority(priority, MARGINS_CONFIG) {
    return MARGINS_CONFIG[priority] || MARGINS_CONFIG.normal;
};

var createFactory = exports.createFactory = function createFactory(_ref5) {
    var appnexus = _ref5.appnexus,
        observer = _ref5.observer,
        marginsConfig = _ref5.marginsConfig,
        nativeRenderer = _ref5.nativeRenderer;
    var placementFn = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : createPlacement;

    var factory = function factory(node) {
        var _getDataAttributes = (0, _attributes.getDataAttributes)(node),
            priority = _getDataAttributes.priority,
            refresh = _getDataAttributes.refresh;

        var refreshable = refresh === 'true';
        var margins = getMarginsForPriority(priority, marginsConfig);

        return placementFn({ node: node, appnexus: appnexus, observer: observer, margins: margins, refreshable: refreshable, nativeRenderer: nativeRenderer }).enable();
    };

    return factory;
};
//# sourceMappingURL=placement.js.map


/*****************
 ** WEBPACK FOOTER
 ** ./~/@spp/ad-renderer/lib/placement.js
 ** module id = 466
 ** module chunks = 1
 **/